import { Button, ButtonRow, FormSection } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { AccountRequestRejectValidated } from '@common/validation/accountRequest';
import type { ValidationErrors } from '@frontend/utils';
import { accountRequestRejectSchema } from '@common/validation/accountRequest';
import { getSelectedLanguage } from '@frontend/translate';
import {
    assertHasAccountRequestAuthorization,
    emptyValidationErrors,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { useAccountRequestContext } from '@frontend/utils/hooks/useAccountRequestContext';
import { AccountRequestRejectionReasonInput } from '../AccountRequestRejectionReasonInput';
import { AccountRequestReviewByOrganizationView } from './AccountRequestReviewByOrganizationViews';

interface AccountRequestRejectingViewProps {
    handleViewChange: (newView: AccountRequestReviewByOrganizationView) => void;
}

export function AccountRequestRejectingView(props: Readonly<AccountRequestRejectingViewProps>) {
    const translate = getSelectedLanguage();
    const { accountRequest, rejectAccountRequest } = useAccountRequestContext();

    const [rejectionReason, setRejectionReason] = useState<string>('');
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    useEffect(() => {
        if (accountRequest.approverPractitionerId) {
            assertHasAccountRequestAuthorization('review', false, accountRequest.approverPractitionerId);
        }
    }, [accountRequest.approverPractitionerId]);

    return (
        <>
            <AccountRequestRejectionReasonInput
                errors={errors}
                layout="vertical"
                onChange={handleChangeRejectionReason}
                rejectionReason={rejectionReason}
            />

            <FormSection
                isBorderless
                sx={{ marginTop: [11, 14] }}
            >
                <ButtonRow>
                    <Button
                        onClick={async () => await reject()}
                        width="full"
                    >
                        {translate.support.send}
                    </Button>
                    <Button
                        onClick={() => props.handleViewChange(AccountRequestReviewByOrganizationView.reviewingRequest)}
                        variant="outline"
                        width="full"
                    >
                        {translate.common.cancel}
                    </Button>
                </ButtonRow>
            </FormSection>
        </>
    );

    function handleChangeRejectionReason(nextRejectionReason: string) {
        setRejectionReason(nextRejectionReason);
        validateOne('rejectionReason', { rejectionReason: nextRejectionReason });
    }

    function validateOne(key: string, object?: AccountRequestRejectValidated) {
        const validationErrors = updateValidationErrorsForOne(accountRequestRejectSchema, object, errors, key);
        setErrors(validationErrors);
    }

    async function reject() {
        const { errors: validationErrors } = validate(accountRequestRejectSchema, {
            rejectionReason,
        });

        if (validationErrors.isEmpty()) {
            await rejectAccountRequest(rejectionReason);

            props.handleViewChange(AccountRequestReviewByOrganizationView.requestDenied);
        } else {
            setErrors(validationErrors);
        }
    }
}
